import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import {
	Client,
	AuthenticationProvider,
} from "@microsoft/microsoft-graph-client";
import { getGraphApiToken } from "./queryFns";
import { isTokenExpired } from "@/common/authentication/jwtDecoder";
import {
	app as microsoftApp,
	authentication as microsoftAuth,
} from "@microsoft/teams-js";
import { authenticateMsal, msalConfig } from "@/common/authentication/msalConfig";

export class GraphClients {
	private static clientInstace: GraphClients;

	public static get instance(): GraphClients {
		if (!GraphClients.clientInstace) {
			GraphClients.clientInstace = new GraphClients();
		}

		return GraphClients.clientInstace;
	}

	public readonly client: TeamsClient;

	private constructor() {
		this.client = new TeamsClient(new GraphAuthenticationProvider());
	}
}

export class TeamsClient {
	private readonly client: Client;

	constructor(authProvider: AuthenticationProvider) {
		this.client = Client.initWithMiddleware({
			authProvider: authProvider,
			debugLogging: false,
		});
	}

	public get<T>(path: string): Promise<T> {
		return this.client.api(path).get();
	}

	public post<T>(path: string, content: any): Promise<T> {
		return this.client.api(path).post(content);
	}
}

export class GraphAuthenticationProvider implements AuthenticationProvider {
	public async getAccessToken(): Promise<string> {
		try {
			const storedToken = Lockr.get(StorageKeys.TokenGraphApi) as string;

			if (!!storedToken && !isTokenExpired(storedToken)) {
				return storedToken;
			}

			let idToken: string;

			if (microsoftApp.isInitialized()) {
				idToken = await microsoftAuth.getAuthToken();
			} else {
				const user = await authenticateMsal({
					scopes: [`${msalConfig.auth.clientId}/.default`],
				});

				if (!user) {
					throw new Error("Failed to acquire user token. Please sign in again.");
				}

				idToken = user.idToken;
			}

			const newToken = await getGraphApiToken(idToken);

			Lockr.set(StorageKeys.TokenGraphApi, newToken);

			return newToken;
		} catch (error: any) {
			Lockr.rm(StorageKeys.TokenGraphApi);
			console.error("getGraphApiToken failed with ", error);

			throw error;
		}
	}
}
